// @mui
import {
  Alert,
  Box,
  Card,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useState } from 'react';
import Iconify from 'src/components/iconify';
import { useUploadDocuments } from 'src/hooks/useUploadDocuments';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// components
import { LoadingButton } from '@mui/lab';
import { useMutation, useQuery } from '@tanstack/react-query';
import { organizationApi } from 'src/api/organization';
import i18n from 'src/locales/i18n';
import { visitCategoriesApi } from 'src/api/visit-categories';
import Editor from 'src/components/editor';
import FormProvider, {
  RHFChipInput,
  RHFMultiSelect,
  RHFPhoneField,
  RHFTextField,
  RHFUploadBox,
} from 'src/components/hook-form';
import { useSnackbar } from 'src/components/snackbar';
import SpecializationRemovalWarningModal from 'src/components/specialization/SpecializationRemovalWarningModal';
import { mapCategoryNameToVisitCategory } from 'src/utils/mapCategoryNameToVisitCategory';
import { mapSpecializationNameIdToString } from 'src/utils/mapSpecializationNameIdToString';
import {
  PHONE_REGEX,
  PHONE_VALIDATION_I18N_KEY,
} from 'src/utils/phoneValidation';
import {
  GetOrganizationDto,
  GetSpecializationsDto,
  UpdateOrganizationDto,
} from 'woofwoof-api';
import { useOrganizationContext } from '../../../hooks/useOrganizationContext';
import OrganizationDetailsInfoCard from '../OrganizationDetailsInfoCard';
import { translateKey } from 'src/locales/translate';


// ----------------------------------------------------------------------

export type OrganizationModalFormValuesProps = {
  logoUrl?: string;
  name: string;
  street: string;
  zip: string;
  city: string;
  website?: string;
  email?: string;
  description?: string;
  phoneNumber: string;
  nip: string;
  isActive: boolean;
  isPublished: boolean;
  isVerified: boolean;
  specializations: string[];
  socialMedia?: string[];
  images?: string[];
};

export type OrganizationDetailsFormProps = {
  fetchOrganization: () => void;
  organization: GetOrganizationDto;
  specializations: GetSpecializationsDto[];
};

export default function OrganizationDetailsForm({
  fetchOrganization,
  organization,
  specializations,
}: OrganizationDetailsFormProps) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { isOrganizationAdminRoleSelected } = useOrganizationContext();

  // Warning modal state
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [affectedProfessionals, setAffectedProfessionals] = useState<
    Array<{
      professionalName: string;
      affectedServices: Array<{
        serviceName: string;
        specializationName: string;
      }>;
    }>
  >([]);
  const [pendingData, setPendingData] =
    useState<OrganizationModalFormValuesProps | null>(null);

  // TODO: If isProfessionalRoleSelected is selected then we should use a separate endpoint that will update a new collection "organizationdetails" entity for this professional
  const { mutateAsync: updateOrganization, isLoading: isUpdateLoading } =
    useMutation({
      mutationFn: (updateOrganizationDto: UpdateOrganizationDto) =>
        organizationApi.organizationControllerUpdateOrganization({
          updateOrganizationDto,
          id: organization._id,
        }),
    });

  const specializationsData = specializations || [];

  const { data: categoriesData } = useQuery(['visitCategories'], () =>
    visitCategoriesApi.visitCategoriesControllerGetAllVisitCategories(),
  );

  const categories = categoriesData?.data || [];
  const categoryIdToNameMap = categories.reduce(
    (acc: Record<string, string>, cat: any) => {
      acc[cat.id] = mapCategoryNameToVisitCategory(cat.name);
      return acc;
    },
    {},
  );

  const specializationsOptions = specializationsData?.map(
    (specialization: any) => ({
      value: specialization.id,
      label: mapSpecializationNameIdToString(specialization),
      group: categoryIdToNameMap[specialization.visitCategoryId] || translateKey("search.specializationOther"),
    }),
  );

  const AddOrganizationSchema: Yup.Schema<OrganizationModalFormValuesProps> =
    Yup.object().shape({
      logoUrl: Yup.string().optional(),
      name: Yup.string().required('Nazwa jest wymagana'),
      street: Yup.string().required(translateKey("validation.streetRequired")),
      zip: Yup.string().required(translateKey("validation.postalCodeRequired")),
      city: Yup.string().required(translateKey("validation.cityRequired")),
      website: Yup.string()
        .optional()
        .transform((value) => {
          if (!value || value === '') return undefined;
          // Add protocol if missing
          if (!/^https?:\/\//i.test(value)) {
            return `https://${value}`;
          }
          return value;
        })
        .test('is-url', translateKey("hardcoded.nieprawidlowy_adres_url"), (value) => {
          if (!value) return true; // optional field
          try {
            new URL(value);
            return true;
          } catch {
            return false;
          }
        }),
      phoneNumber: Yup.string()
        .required('Numer telefonu jest wymagany')
        .matches(PHONE_REGEX, () => i18n.t(PHONE_VALIDATION_I18N_KEY)),
      nip: Yup.string().required('NIP jest wymagany'),
      isActive: Yup.boolean().required('Status jest wymagany'),
      socialMedia: Yup.array().of(Yup.string().required()).optional(),
      isPublished: Yup.boolean().required(),
      isVerified: Yup.boolean().required(),
      specializations: Yup.array()
        .of(Yup.string().required())
        .min(1, translateKey("hardcoded.wybierz_przynajmniej_jedna_specjalizacje"))
        .required(translateKey("hardcoded.specjalizacje_sa_wymagane")),
      email: Yup.string().optional().email(translateKey("hardcoded.nieprawidlowy_adres_email")),
      description: Yup.string().optional(),
      images: Yup.array().of(Yup.string().required()).optional(),
    });

  const defaultValues: OrganizationModalFormValuesProps = {
    logoUrl: organization?.logoUrl || '',
    name: organization?.name || '',
    street: organization?.street || '',
    zip: organization?.zip || '',
    city: organization?.city || '',
    website: organization?.website || '',
    email: organization?.email || '',
    phoneNumber: organization?.phoneNumber || '',
    nip: organization?.nip?.toString() || '',
    isActive: organization?.isActive || false,
    isPublished: organization?.isPublished || false,
    isVerified: organization?.isVerified || false,
    description: organization?.description || '',
    specializations: organization?.specializations || [],
    socialMedia: organization?.socialMedia || [],
    images: organization?.images || [],
  };

  const methods = useForm<OrganizationModalFormValuesProps>({
    resolver: yupResolver(AddOrganizationSchema),
    defaultValues,
    values: defaultValues,
  });

  const {
    setValue,
    handleSubmit,
    getValues,
    formState: { errors },
  } = methods;

  const formValues = getValues();

  const handleConfirmRemoval = async () => {
    if (!pendingData) return;

    setShowWarningModal(false);

    try {
      // Clean up empty optional fields
      const cleanedData: any = { ...pendingData };

      if (!cleanedData.email || cleanedData.email.trim() === '') {
        delete cleanedData.email;
      }
      if (!cleanedData.website || cleanedData.website.trim() === '') {
        delete cleanedData.website;
      }
      if (!cleanedData.logoUrl || cleanedData.logoUrl.trim() === '') {
        delete cleanedData.logoUrl;
      }
      if (!cleanedData.description || cleanedData.description.trim() === '') {
        delete cleanedData.description;
      }
      delete cleanedData.isPublished;
      delete cleanedData.isVerified;

      await updateOrganization(cleanedData);

      enqueueSnackbar(translateKey("hardcoded.organizacja_zostala_zaktualizowana"), {
        variant: 'success',
      });
      fetchOrganization();
    } catch (error) {
      enqueueSnackbar(error.message || error, {
        variant: 'error',
      });
    } finally {
      setPendingData(null);
      setAffectedProfessionals([]);
    }
  };

  const handleCancelRemoval = () => {
    setShowWarningModal(false);
    setPendingData(null);
    setAffectedProfessionals([]);
  };

  const [isPublishing, setIsPublishing] = useState(false);
  const handleTogglePublished = async (checked: boolean) => {
    if (!organization._id) return;
    setIsPublishing(true);
    try {
      await organizationApi.organizationControllerSetOrganizationPublished({
        id: organization._id,
        setOrganizationPublishedDto: { isPublished: checked },
      });
      enqueueSnackbar(translateKey("hardcoded.widocznosc_zostala_zaktualizowana"), {
        variant: 'success',
      });
      fetchOrganization();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        translateKey("hardcoded.nie_udalo_sie_zaktualizowac_widocznosci");
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleOnSubmit = async (data: OrganizationModalFormValuesProps) => {
    // Check if any specializations are being removed
    const currentSpecs = organization?.specializations || [];
    const newSpecs = data.specializations;
    const removedSpecs = currentSpecs.filter((id) => !newSpecs.includes(id));

    if (removedSpecs.length > 0) {
      // Check if removed specializations have assigned services across organization
      try {
        const response =
          await organizationApi.organizationControllerCheckOrganizationSpecializationServices(
            {
              id: organization._id,
              checkSpecializationServicesDto: {
                specializationIds: removedSpecs,
              },
            },
          );

        if (response.data?.hasAssignedServices) {
          // Show warning modal
          setAffectedProfessionals(response.data.professionals);
          setPendingData(data);
          setShowWarningModal(true);
          return;
        }
      } catch (error) {
        console.error(
          'Failed to check organization specialization services:',
          error,
        );
        // Continue with save if check fails
      }
    }

    // No removed specializations or no affected services, proceed directly
    try {
      // Clean up empty optional fields to avoid backend validation errors
      const cleanedData: any = { ...data };

      // Remove empty strings for optional fields
      if (!cleanedData.email || cleanedData.email.trim() === '') {
        delete cleanedData.email;
      }
      if (!cleanedData.website || cleanedData.website.trim() === '') {
        delete cleanedData.website;
      }
      if (!cleanedData.logoUrl || cleanedData.logoUrl.trim() === '') {
        delete cleanedData.logoUrl;
      }
      if (!cleanedData.description || cleanedData.description.trim() === '') {
        delete cleanedData.description;
      }

      console.log('Cleaned submission data:', cleanedData);

      await updateOrganization(cleanedData);

      enqueueSnackbar(translateKey("hardcoded.organizacja_zostala_zaktualizowana"), {
        variant: 'success',
      });
      fetchOrganization();
    } catch (error) {
      console.error('Update organization error:', error);
      enqueueSnackbar(error.message || error, {
        variant: 'error',
      });
    }
  };

  // Image upload logic
  const [uploading, setUploading] = useState(false);
  const { upload, downloadURLs, remove, error, setInitialFiles } =
    useUploadDocuments({ usePublicEndpoint: true });

  const handleImagesDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const currentImages = getValues('images') || [];
      const isFileAlreadyUploaded = acceptedFiles.some((file) =>
        downloadURLs.some((downloadFile) => downloadFile.name === file.name),
      );

      if (isFileAlreadyUploaded) {
        enqueueSnackbar(translateKey("hardcoded.zdjecie_zostalo_juz_dodane"), {
          variant: 'error',
        });
        return;
      }
      setUploading(true);
      await upload(acceptedFiles);
      setUploading(false);
    },
    [upload, downloadURLs, enqueueSnackbar, getValues],
  );

  const handleImageRemove = async (fileName: string) => {
    try {
      // Remove from Firebase storage
      remove(fileName);

      // Update local form state
      const updatedImages = getValues('images')?.filter(
        (image) => image !== fileName,
      );
      setValue('images', updatedImages);

      // Immediately update the database
      await updateOrganization({
        images: updatedImages,
      });

      enqueueSnackbar(translateKey("hardcoded.zdjecie_zostalo_usuniete"), {
        variant: 'success',
      });

      // Refresh organization data to sync with backend
      fetchOrganization();
    } catch (error) {
      enqueueSnackbar(translateKey("hardcoded.nie_udalo_sie_usunac_zdjecia"), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    if (organization?.images && organization.images.length > 0) {
      if (setInitialFiles) {
        const initialFiles = organization.images.map((imageUrl) => ({
          name: imageUrl,
          url: imageUrl,
          progress: 100,
        }));
        setInitialFiles(initialFiles);
      }
    }
  }, [organization, setInitialFiles]);

  useEffect(() => {
    const updateImagesInDB = async () => {
      if (!uploading && downloadURLs.some((file) => file.url)) {
        const currentImages = getValues('images') || [];

        // Get all URLs from downloadURLs in their original order
        const allDownloadURLs = downloadURLs
          .filter((file) => file.url)
          .map((file) => file.url);

        // Keep only URLs that exist in downloadURLs (preserving their order)
        const existingImages = currentImages.filter((img) =>
          allDownloadURLs.includes(img),
        );

        // Add new images that aren't already in currentImages
        const newImages = allDownloadURLs.filter(
          (url) => !currentImages.includes(url),
        );

        // Merge: existing images first, then new images (maintains upload order)
        const mergedImages = [...existingImages, ...newImages];

        setValue('images', mergedImages);

        // If there are new images, update the database
        if (newImages.length > 0) {
          try {
            await updateOrganization({
              images: mergedImages,
            });

            enqueueSnackbar(translateKey("hardcoded.zdjecia_zostaly_dodane"), {
              variant: 'success',
            });

            // Refresh organization data to sync with backend
            fetchOrganization();
          } catch (error) {
            enqueueSnackbar(translateKey("hardcoded.nie_udalo_sie_zapisac_zdjec"), {
              variant: 'error',
            });
          }
        }
      }
    };

    updateImagesInDB();
  }, [
    uploading,
    downloadURLs,
    setValue,
    getValues,
    updateOrganization,
    enqueueSnackbar,
    fetchOrganization,
  ]);

  useEffect(() => {
    if (error) {
      setUploading(false);
    }
  }, [error]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(handleOnSubmit)}>
      {!organization.isVerified && (
        <Alert severity="info" sx={{ mb: 2 }}>{translateKey("hardcoded.trwa_weryfikacja_ograniczona_funkcjonalnosc")}</Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={4} xl={4}>
          <OrganizationDetailsInfoCard isLoading={isUpdateLoading} />
        </Grid>

        <Grid item xs={12} md={12} lg={8} xl={8}>
          {isOrganizationAdminRoleSelected && (
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1">{translateKey("hardcoded.widocznosc_placowki")}</Typography>
                <Tooltip
                  placement="top"
                  arrow
                  title={
                    <Box sx={{ p: 0.5, maxWidth: 320 }}>
                      <Typography variant="body2" fontWeight={600} gutterBottom>{translateKey("hardcoded.jak_kontrolowac_widocznosc_placowki")}</Typography>
                      <Typography variant="body2">{translateKey("hardcoded.tym_przyciskiem_mozesz_kontrolowac_widocznosc_placowki_w_wynikach")}</Typography>
                    </Box>
                  }
                >
                  <IconButton size="small">
                    <Iconify icon="eva:info-outline" />
                  </IconButton>
                </Tooltip>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={organization.isPublished}
                    onChange={(e) => handleTogglePublished(e.target.checked)}
                    disabled={
                      isUpdateLoading ||
                      isPublishing ||
                      !organization.isVerified
                    }
                  />
                }
                label={
                  organization.isVerified
                    ? organization.isPublished
                      ? 'Widoczna'
                      : 'Ukryta'
                    : 'Weryfikacja w toku'
                }
              />
            </Box>
          )}
          <Card
            sx={{
              p: { xs: 2, sm: 2, md: 3, lg: 3, xl: 3 },
              display: 'flex',
              flex: 1,
              boxShadow: theme.customShadows.z24,
            }}
          >
            <Stack sx={{ height: 1, flex: 1 }}>
              <Stack
                sx={{
                  pb: 3,
                  gap: 3,
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: { xs: 3, md: 2 },
                  }}
                >
                  {isOrganizationAdminRoleSelected && (
                    <>
                      <RHFPhoneField
                        required
                        name="phoneNumber"
                        label={translateKey("auth.fieldPhone")}
                        disabled={isUpdateLoading}
                      />

                      <RHFTextField
                        name="email"
                        label="Adres email"
                        disabled={isUpdateLoading}
                      />

                      <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                        <RHFTextField
                          name="website"
                          label="Strona internetowa"
                          disabled={isUpdateLoading}
                        />
                      </Box>
                    </>
                  )}

                  <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                    <RHFMultiSelect
                      chip
                      required
                      name="specializations"
                      label="Specjalizacje"
                      disabled={isUpdateLoading}
                      options={specializationsOptions}
                      sx={{ width: '100%' }}
                    />
                  </Box>

                  <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                    <RHFChipInput
                      name="socialMedia"
                      label="Social Media"
                      disabled={isUpdateLoading}
                    />
                  </Box>

                  <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                    <Editor
                      sx={{ minHeight: '500px' }}
                      disabled={isUpdateLoading}
                      placeholder={translateKey("hardcoded.opis_placowki")}
                      simple
                      id="simple-editor"
                      value={formValues.description}
                      onChange={(_value) => {
                        if (_value.replace(/<(.|\n)*?>/g, '').trim()) {
                          setValue('description', _value);
                        } else {
                          setValue('description', '');
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                    <Stack gap={2}>
                      <Typography variant="subtitle2">{translateKey("hardcoded.zdjecia_placowki")}</Typography>

                      <RHFUploadBox
                        name="images"
                        maxSize={5242880}
                        accept={{
                          'image/png': ['.png'],
                          'image/jpeg': ['.jpg', '.jpeg'],
                          'image/webp': ['.webp'],
                        }}
                        onDrop={handleImagesDrop}
                        placeholder={
                          <Stack sx={{ gap: 0.5, p: 3 }} alignItems="center">
                            {!uploading ? (
                              <>
                                <Iconify
                                  icon="eva:cloud-upload-fill"
                                  width={40}
                                />
                                <Typography variant="body2">{translateKey("hardcoded.dodaj_zdjecia_placowki")}</Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Maksymalny rozmiar: 5 MB
                                </Typography>
                              </>
                            ) : null}
                          </Stack>
                        }
                        disabled={uploading || isUpdateLoading}
                        sx={{
                          width: '100%',
                          minHeight: 150,
                          borderRadius: 1.5,
                          m: 0,
                        }}
                      />

                      {downloadURLs.length > 0 && (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns:
                              'repeat(auto-fill, minmax(80px, 1fr))',
                            gap: 1.5,
                          }}
                        >
                          {downloadURLs.map(({ name, progress, url }) => (
                            <Box
                              key={name}
                              sx={{
                                position: 'relative',
                                paddingTop: '100%',
                                borderRadius: 1.5,
                                overflow: 'hidden',
                                border: (theme) =>
                                  `1px solid ${theme.palette.divider}`,
                              }}
                            >
                              {progress === 100 && url ? (
                                <>
                                  <Box
                                    component="img"
                                    src={url}
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                    }}
                                  />
                                  <IconButton
                                    onClick={() => handleImageRemove(name)}
                                    disabled={uploading}
                                    sx={{
                                      position: 'absolute',
                                      top: 2,
                                      right: 2,
                                      width: 20,
                                      height: 20,
                                      padding: 0,
                                      backgroundColor: 'background.paper',
                                      boxShadow: 1,
                                      '&:hover': {
                                        backgroundColor: 'error.lighter',
                                        color: 'error.main',
                                      },
                                    }}
                                  >
                                    <Iconify icon="eva:close-fill" width={14} />
                                  </IconButton>
                                </>
                              ) : (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'background.neutral',
                                  }}
                                >
                                  <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{ width: '80%' }}
                                  />
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Box>
              </Stack>

              <Stack
                sx={{
                  flex: 1,
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                }}
              >
                <Stack>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    data-testid="details-save"
                    loading={isUpdateLoading}
                    onClick={() => console.log('Save button clicked')}
                  >{translateKey("common.save")}</LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      <SpecializationRemovalWarningModal
        open={showWarningModal}
        onClose={handleCancelRemoval}
        onConfirm={handleConfirmRemoval}
        affectedServices={[]}
        isProfessional={false}
        affectedProfessionals={affectedProfessionals}
      />
    </FormProvider>
  );
}
